// ERP business data persistence on ThingsBoard.
//
// Each ERP collection (inventory, sales, purchase, employees, production, BOMs,
// stock movements) is stored as a single JSON value under a SERVER_SCOPE
// attribute on the logged-in Customer entity — the same mechanism the app
// already uses for shift/config data (see masterservice.js).
//
// Reads use the customer-user token; writes use the tenant token (token1),
// mirroring shiftadd(), because writing customer attributes needs admin scope.
import axios from 'axios';
import { cleanCustomerId } from './masterservice';

const readClient = axios.create({ baseURL: window._env_.SERVER_URL });
readClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers['X-Authorization'] = `Bearer ${token}`;
  return config;
});

const writeClient = axios.create({ baseURL: window._env_.SERVER_URL });
writeClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token1') || localStorage.getItem('token');
  if (token) config.headers['X-Authorization'] = `Bearer ${token}`;
  return config;
});

// Attribute keys for each ERP collection.
export const ERP_KEYS = {
  inventory: 'erp_inventory',
  sales: 'erp_sales',
  purchase: 'erp_purchase',
  employees: 'erp_employees',
  production: 'erp_production',
  boms: 'erp_boms',
  movements: 'erp_stock_movements',
  crm: 'erp_crm',
  finance: 'erp_finance',
};

const baseUrl = () => window._env_.SERVER_URL.replace(/\/$/, '');
const getCustomerId = () => cleanCustomerId(localStorage.getItem('CustomerID'));

// Read a server-scope attribute and return its parsed JSON value (or `fallback`).
export const getErpData = async (key, fallback = []) => {
  try {
    const url = `${baseUrl()}/api/plugins/telemetry/CUSTOMER/${getCustomerId()}/values/attributes?keys=${key}`;
    const { data } = await readClient.get(url);
    const entry = Array.isArray(data) ? data.find((d) => d.key === key) : null;
    if (!entry || entry.value === undefined || entry.value === null) return fallback;
    let value = entry.value;
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch (e) { /* leave as-is */ }
    }
    return value ?? fallback;
  } catch (error) {
    console.error(`Error reading ERP attribute "${key}":`, error);
    throw error;
  }
};

// Persist a JSON-serialisable value under a server-scope attribute.
export const saveErpData = async (key, value) => {
  try {
    const url = `${baseUrl()}/api/plugins/telemetry/CUSTOMER/${getCustomerId()}/SERVER_SCOPE`;
    await writeClient.post(url, { [key]: value });
    return value;
  } catch (error) {
    console.error(`Error saving ERP attribute "${key}":`, error);
    throw error;
  }
};
